import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AppLayout } from '../../layouts/AppLayout';
import { PageHeader } from '../../layouts/PageHeader';
import { useTopClientsRevenue } from '../../../hooks/useTopClientsRevenue';
import {
  getAvailableFYYears,
  processClients,
  computeKPIs,
  buildConcentrationChartData,
  getPeriodLabel,
  getActivitySummary,
  getQuarterMonthLabel,
} from '../../../utils/topClientsUtils';
import { KpiStrip } from './KpiStrip';
import { ClientRankingList } from './ClientRankingList';
import { ConcentrationChart } from './ConcentrationChart';

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-md p-4 animate-pulse">
    <div className="h-2 w-20 bg-slate-200 rounded mb-3" />
    <div className="h-6 w-28 bg-slate-200 rounded" />
  </div>
);

const LoadingSkeleton: React.FC = () => (
  <div className="p-6 space-y-4">
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
    </div>
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-md h-96 animate-pulse" />
      <div className="bg-white rounded-xl border border-slate-200 shadow-md h-80 animate-pulse" />
    </div>
  </div>
);

// ─── Filter select helper ─────────────────────────────────────────────────────
const FilterSelect: React.FC<{
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  options: { value: string | number; label: string }[];
  disabled?: boolean;
}> = ({ label, value, onChange, options, disabled }) => (
  <div className="flex items-center gap-1">
    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">{label}</span>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      className="text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

const FY_QUARTERS = [
  { value: 'all', label: 'All Quarters' },
  { value: 'Q1', label: 'Q1 (Apr–Jun)' },
  { value: 'Q2', label: 'Q2 (Jul–Sep)' },
  { value: 'Q3', label: 'Q3 (Oct–Dec)' },
  { value: 'Q4', label: 'Q4 (Jan–Mar)' },
];

// ─── Filter summary bar ───────────────────────────────────────────────────────
interface FilterSummaryBarProps {
  topN: number;
  selectedFYYear: string;
  selectedQuarter: string;
  isFYQuarterMode: boolean;
  sortedClientCount: number;
  periodLabel: string;
  dateRange: string;
  activeCount: number;
  inactiveCount: number;
}

const FilterSummaryBar: React.FC<FilterSummaryBarProps> = ({
  topN,
  selectedFYYear,
  selectedQuarter,
  isFYQuarterMode,
  sortedClientCount,
  periodLabel,
  dateRange,
  activeCount,
  inactiveCount,
}) => {
  const analysisDesc = isFYQuarterMode
    ? `Top ${topN} clients for ${periodLabel}`
    : selectedFYYear !== 'all' || selectedQuarter !== 'all'
    ? `Top ${topN} clients filtered by ${selectedFYYear !== 'all' ? selectedFYYear : 'All FY'}${selectedQuarter !== 'all' ? ` ${selectedQuarter} (${getQuarterMonthLabel(selectedQuarter)})` : ''}`
    : `Top ${topN} clients across all years`;

  const rankDesc = isFYQuarterMode ? `FY Quarter Revenue (${periodLabel})` : 'Lifetime Revenue';

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5">
      <div className="flex items-center justify-between flex-wrap gap-2 text-[11px]">
        <span className="text-slate-600">
          <span className="font-semibold">Analysis:</span>{' '}
          {analysisDesc} · Ranked by {rankDesc}
        </span>
        <span className="text-slate-500">
          {isFYQuarterMode ? (
            <>
              <span className="font-semibold">Period:</span>{' '}
              {periodLabel} · {sortedClientCount} client records
            </>
          ) : (
            <>
              <span className="font-semibold">Activity Range:</span>{' '}
              {dateRange}{' '}
              <span className="text-slate-400">
                ({activeCount} active, {inactiveCount} inactive)
              </span>
            </>
          )}
        </span>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
export const TopClientsRevenuePage: React.FC = () => {
  const { records, loading, error } = useTopClientsRevenue();

  // ── Filter state ────────────────────────────────────────────────────────────
  const [topN, setTopN] = useState<number>(10);
  const [selectedFYYear, setSelectedFYYear] = useState<string>('all');
  const [selectedQuarter, setSelectedQuarter] = useState<string>('all');

  // ── Available FY years ──────────────────────────────────────────────────────
  const availableFYYears = useMemo(() => getAvailableFYYears(records), [records]);

  // ── Auto-select latest FY on data load (one-time) ───────────────────────────
  const fyInitialisedRef = useRef(false);
  useEffect(() => {
    if (!fyInitialisedRef.current && availableFYYears.length > 0) {
      setSelectedFYYear(availableFYYears[0]); // sorted desc, [0] = latest
      fyInitialisedRef.current = true;
    }
  }, [availableFYYears]);

  // ── FY change: cascade reset quarter ────────────────────────────────────────
  const handleFYYearChange = (v: string) => {
    setSelectedFYYear(v);
    setSelectedQuarter('all');
  };

  // ── Process clients ─────────────────────────────────────────────────────────
  const { clients: sortedClients, usedFallback, isFYQuarterMode } = useMemo(
    () => processClients(records, selectedFYYear, selectedQuarter),
    [records, selectedFYYear, selectedQuarter]
  );

  const topNClients = useMemo(() => sortedClients.slice(0, topN), [sortedClients, topN]);

  const kpis = useMemo(
    () => computeKPIs(sortedClients, topN),
    [sortedClients, topN]
  );

  const chartData = useMemo(
    () => buildConcentrationChartData(kpis, topN, sortedClients),
    [kpis, topN, sortedClients]
  );

  const periodLabel = useMemo(
    () => getPeriodLabel(selectedFYYear, selectedQuarter, isFYQuarterMode),
    [selectedFYYear, selectedQuarter, isFYQuarterMode]
  );

  const activitySummary = useMemo(
    () => getActivitySummary(sortedClients),
    [sortedClients]
  );

  // ── Filter bar ──────────────────────────────────────────────────────────────
  const filterBar = (
    <div className="flex items-center gap-2 flex-wrap">
      <FilterSelect
        label="Display"
        value={topN}
        onChange={v => setTopN(parseInt(v, 10))}
        options={[
          { value: 5,  label: 'Top 5'  },
          { value: 10, label: 'Top 10' },
          { value: 15, label: 'Top 15' },
          { value: 20, label: 'Top 20' },
        ]}
      />

      <div className="h-4 w-px bg-slate-200" />

      <FilterSelect
        label="FY"
        value={selectedFYYear}
        onChange={handleFYYearChange}
        options={[
          { value: 'all', label: 'All FYs (lifetime)' },
          ...availableFYYears.map(fy => ({ value: fy, label: fy })),
        ]}
      />

      <div className="h-4 w-px bg-slate-200" />

      <FilterSelect
        label="Quarter"
        value={selectedQuarter}
        onChange={setSelectedQuarter}
        options={FY_QUARTERS}
        disabled={selectedFYYear === 'all'}
      />

      {usedFallback && (
        <>
          <div className="h-4 w-px bg-slate-200" />
          <span className="text-[11px] font-medium text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
            Showing lifetime revenue (no data for selected period)
          </span>
        </>
      )}
    </div>
  );

  // ── Error ───────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <AppLayout>
        <PageHeader title="Top Clients by Revenue" />
        <div className="p-6 flex items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <p className="text-red-600 font-medium mb-1">Failed to load data</p>
            <p className="text-slate-600 text-sm">{error}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader
        title="Top Clients by Revenue"
        subtitle={periodLabel}
        filters={filterBar}
      />

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <div className="p-6 space-y-4">
          {/* Filter summary bar */}
          <FilterSummaryBar
            topN={topN}
            selectedFYYear={selectedFYYear}
            selectedQuarter={selectedQuarter}
            isFYQuarterMode={isFYQuarterMode}
            sortedClientCount={sortedClients.length}
            periodLabel={periodLabel}
            dateRange={activitySummary.dateRange}
            activeCount={activitySummary.activeCount}
            inactiveCount={activitySummary.inactiveCount}
          />

          {/* KPI strip */}
          <KpiStrip
            kpis={kpis}
            topN={topN}
            isFYQuarterMode={isFYQuarterMode}
            periodLabel={periodLabel}
          />

          {/* Two-column: ranking list + concentration chart */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            <ClientRankingList
              clients={topNClients}
              totalRevenue={kpis.totalRevenue}
              isFYQuarterMode={isFYQuarterMode}
              topN={topN}
              periodLabel={periodLabel}
            />
            <ConcentrationChart
              data={chartData}
              topN={topN}
              isFYQuarterMode={isFYQuarterMode}
              periodLabel={periodLabel}
            />
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default TopClientsRevenuePage;
